import api, { apiOrigin } from './client';

// ==================== Analysis ====================

/**
 * Upload running FIT file for analysis
 * @param {File} file - FIT file to upload
 * @param {Object} params - Analysis parameters
 * @param {File|null} gpxFile - Optional GPX file for race simulation
 * @returns {Promise} Analysis response data
 */
export const uploadRunningFit = async (file, params = {}, gpxFile = null) => {
  const {
    body_weight = 70.0,
    threshold_pace = 5.0,
    threshold_heart_rate = null,
    max_heart_rate = 185,
    resting_heart_rate = null,
    what_if_pacing_strategy = null,
    reused_file_id = null,
    race_id = null,
    snap_to_gpx = false,
    apply_gps_corrections = true,
    description = null,
  } = params;

  const formData = new FormData();

  // Only append file if it's not null (when reusing, we don't upload the file)
  if (file) {
    formData.append('file', file);
  }

  if (reused_file_id) {
    formData.append('reused_file_id', reused_file_id);
  }

  if (gpxFile) {
    formData.append('gpx_file', gpxFile);
  }

  formData.append('body_weight', body_weight);
  formData.append('threshold_pace', threshold_pace);

  if (threshold_heart_rate !== null) {
    formData.append('threshold_heart_rate', threshold_heart_rate);
  }

  formData.append('max_heart_rate', max_heart_rate);

  if (resting_heart_rate !== null) {
    formData.append('resting_heart_rate', resting_heart_rate);
  }

  if (what_if_pacing_strategy) {
    formData.append('what_if_pacing_strategy', what_if_pacing_strategy);
  }

  if (race_id !== null) {
    formData.append('race_id', race_id);
  }

  // Convert booleans to "true"/"false" strings for FastAPI Form parsing
  formData.append('snap_to_gpx', snap_to_gpx ? 'true' : 'false');
  formData.append('apply_gps_corrections', apply_gps_corrections ? 'true' : 'false');
  if (description !== null && description !== '') {
    formData.append('description', description);
  }

  // Log all FormData entries for debugging
  console.log('[uploadRunningFit] Called with file:', file, 'reused_file_id:', reused_file_id);
  console.log('[uploadRunningFit] FormData contents:');
  for (let [key, value] of formData.entries()) {
    console.log(`  ${key}:`, value);
  }

  const response = await api.post('/running/upload-fit', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

/**
 * Simulate race with GPX course
 * @param {File} fitFile - Running FIT file
 * @param {File} gpxFile - GPX course file
 * @param {Object} params - Simulation parameters
 * @returns {Promise} Race simulation results
 */
export const simulateRunningRace = async (fitFile, gpxFile, params = {}) => {
  const {
    body_weight = 70.0,
    threshold_pace = 5.0,
    threshold_heart_rate = null,
    max_heart_rate = 185,
    resting_heart_rate = null
  } = params;

  const formData = new FormData();
  formData.append('fit_file', fitFile);
  formData.append('gpx_file', gpxFile);
  formData.append('body_weight', body_weight);
  formData.append('threshold_pace', threshold_pace);

  if (threshold_heart_rate !== null) {
    formData.append('threshold_heart_rate', threshold_heart_rate);
  }

  formData.append('max_heart_rate', max_heart_rate);

  if (resting_heart_rate !== null) {
    formData.append('resting_heart_rate', resting_heart_rate);
  }

  const response = await api.post('/running/simulate-race', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

/**
 * Build (or return cached) race plan segments for a running analysis session.
 * @param {string} sessionId - Analysis session ID
 * @returns {Promise<{race_plan_segments: Array}>}
 */
export const buildRacePlan = async (sessionId) => {
  const response = await api.post(`/running/analysis/${sessionId}/race-plan`);
  return response.data;
};

/**
 * Replace the user's manual checkpoints for an analysis session.
 *
 * The whole list is sent every time: it is small, entirely user-authored, and
 * replacing it keeps the call idempotent. The server drops the cached plan so
 * the next buildRacePlan() rebuilds with these merged in.
 * @param {string} sessionId
 * @param {Array} checkpoints - {id?, name, distance_m, target_stop_s?, services?, cutoff_time?}
 */
/**
 * Suggest which catalogue race a just-picked FIT file belongs to.
 *
 * Reads only the start position and total distance server-side, so it is cheap
 * enough to run the moment a file is chosen. Returns {candidates: [...]}, empty
 * when nothing matches — never an error the caller has to handle.
 */
export const detectRaceForFit = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('/running/detect-race', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const setAnalysisManualCheckpoints = async (sessionId, checkpoints) => {
  const response = await api.put(
    `/running/analysis/${sessionId}/manual-checkpoints`, { checkpoints });
  return response.data;
};

/** Manual checkpoints for a prediction (see the analysis variant). */
export const setPredictionManualCheckpoints = async (predictionId, checkpoints) => {
  const response = await api.put(
    `/running/predictions/${predictionId}/manual-checkpoints`, { checkpoints });
  return response.data;
};

/**
 * Propose a name for a point on the route, with ranked alternatives.
 *
 * Cache-only on the server — it never goes out to OpenStreetMap live, so it is
 * safe to call repeatedly as a picker marker moves. An empty `name` means
 * nothing is cached nearby, not that the point is invalid.
 * @returns {Promise<{distance_m, lat, lon, name, candidates: Array}>}
 */
export const getCheckpointNameSuggestion = async (sessionId, distanceM) => {
  const response = await api.get(
    `/running/analysis/${sessionId}/poi-suggestion`, { params: { distance_m: distanceM } });
  return response.data;
};

/**
 * Build (or return cached) race plan segments for a race prediction session.
 * @param {string} predictionId - Prediction session ID
 * @returns {Promise<{race_plan_segments: Array}>}
 */
export const buildPredictionRacePlan = async (predictionId) => {
  const response = await api.post(`/running/predictions/${predictionId}/race-plan`);
  return response.data;
};

/**
 * Export GPX segment from running analysis session
 * @param {number} analysisSessionId - Analysis session ID
 * @returns {Promise} GPX file blob
 */
export const exportRunningGpx = async (analysisSessionId) => {
  const response = await api.get(`/running/export-gpx/${analysisSessionId}`, {
    responseType: 'blob'
  });

  return response;
};

/**
 * Download race plan as a Garmin FIT structured workout file (analysis session).
 * @param {string} sessionId - Running analysis session ID
 * @returns {Promise} FIT file blob response
 */
export const downloadPlanFit = async (sessionId) => {
  const response = await api.post(
    `/running/analysis/${sessionId}/download-plan-fit`,
    {},
    { responseType: 'blob' }
  );
  return response;
};

/**
 * Download race plan as a Garmin FIT structured workout file (prediction session).
 * @param {string} predictionId - Running prediction session ID
 * @returns {Promise} FIT file blob response
 */
export const downloadPredictionPlanFit = async (predictionId) => {
  const response = await api.post(
    `/running/predictions/${predictionId}/download-plan-fit`,
    {},
    { responseType: 'blob' }
  );
  return response;
};

/**
 * Download race plan as a GPX course file for Garmin/Coros watch (analysis session).
 * @param {string} sessionId - Running analysis session ID
 * @returns {Promise} GPX file blob response
 */
export const downloadWatchGpx = async (sessionId) => {
  const response = await api.post(
    `/running/analysis/${sessionId}/download-watch-gpx`,
    {},
    { responseType: 'blob' }
  );
  return response;
};

/**
 * Download race plan as a GPX course file for Garmin/Coros watch (prediction session).
 * @param {string} predictionId - Running prediction session ID
 * @returns {Promise} GPX file blob response
 */
export const downloadPredictionWatchGpx = async (predictionId) => {
  const response = await api.post(
    `/running/predictions/${predictionId}/download-watch-gpx`,
    {},
    { responseType: 'blob' }
  );
  return response;
};

/**
 * Rebuild the watch URL against the origin the API is actually reachable on.
 *
 * The backend can only guess its own public origin from env vars, and in dev it
 * guesses the frontend's (:4215) — which serves index.html for unknown paths, so
 * the wrong URL returns HTML with a 200 instead of failing. The browser has
 * already proven which origin works, so trust that and overwrite `url`.
 */
const withAbsoluteUrl = (link) =>
  (link && link.path) ? { ...link, url: apiOrigin() + link.path } : link;

/**
 * mkpaceLIVE — publish a prediction's race plan to the watch.
 *
 * A published URL is an immutable pointer to one plan at one granularity. The
 * watch caches the plan on first fetch and only drops that cache when the URL in
 * its Connect IQ settings changes, so a URL is never rewritten in place — that
 * would leave the runner racing a superseded plan.
 *
 * `replace = false` (default) is SAFE: an existing link is returned untouched,
 * so this can be called just to read the URL. `replace = true` revokes the old
 * token and mints a new one, which is what forces the re-paste and the re-sync.
 *
 * Requires the race plan to be built already (the Plan tab does this on open).
 *
 * @param {string} predictionId
 * @param {'detailed'|'minimal'} granularity - the Plan tab toggle's current value
 * @param {boolean} replace - true to revoke the existing link and mint a new one
 * @returns {Promise<{token: string, url: string, granularity: string}>}
 */
export const createPredictionWatchPlan = async (predictionId, granularity = 'detailed', replace = false) => {
  const response = await api.post(
    `/running/predictions/${predictionId}/watch-plan`,
    { granularity, replace }
  );
  return withAbsoluteUrl(response.data);
};

/** Same, for an analysis session's race plan. */
export const createAnalysisWatchPlan = async (sessionId, granularity = 'detailed', replace = false) => {
  const response = await api.post(
    `/running/analysis/${sessionId}/watch-plan`,
    { granularity, replace }
  );
  return withAbsoluteUrl(response.data);
};

/** This prediction's published watch URL: `{ link }`, or `{ link: null }`. */
export const getPredictionWatchPlan = async (predictionId) => {
  const response = await api.get(`/running/predictions/${predictionId}/watch-plan`);
  return { ...response.data, link: withAbsoluteUrl(response.data.link) };
};

/** This analysis session's published watch URL: `{ link }` or `{ link: null }`. */
export const getAnalysisWatchPlan = async (sessionId) => {
  const response = await api.get(`/running/analysis/${sessionId}/watch-plan`);
  return { ...response.data, link: withAbsoluteUrl(response.data.link) };
};

/** Unpublish the watch URL — the watch gets a 404 on its next sync. */
export const revokeWatchPlan = async (token) => {
  const response = await api.delete(`/running/watch-plan/${token}`);
  return response.data;
};

// ==================== Workouts ====================

/**
 * Generate running workout from analysis session
 * @param {Object} params - Workout generation parameters
 * @returns {Promise} Workout steps data
 */
export const generateRunningWorkout = async (params) => {
  const {
    analysis_session_id,
    start_time,
    end_time,
    threshold_pace,
    include_warmup = false,
    warmup_duration = 600,
    pace_ratio = 1.0
  } = params;

  const formData = new FormData();
  formData.append('analysis_session_id', analysis_session_id);
  formData.append('start_time', start_time);
  formData.append('end_time', end_time);
  formData.append('threshold_pace', threshold_pace);
  formData.append('include_warmup', include_warmup);
  formData.append('warmup_duration', warmup_duration);
  formData.append('pace_ratio', pace_ratio);

  const response = await api.post('/running/generate-workout', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

/**
 * Download Zwift running workout file (.zwo format)
 * @param {Object} params - Workout parameters
 * @returns {Promise} ZWO file blob
 */
export const downloadRunningZwiftWorkout = async (params) => {
  const {
    analysis_session_id,
    start_time,
    end_time,
    threshold_pace,
    include_warmup = false,
    warmup_duration = 600,
    pace_ratio = 1.0
  } = params;

  const formData = new FormData();
  formData.append('analysis_session_id', analysis_session_id);
  formData.append('start_time', start_time);
  formData.append('end_time', end_time);
  formData.append('threshold_pace', threshold_pace);
  formData.append('include_warmup', include_warmup);
  formData.append('warmup_duration', warmup_duration);
  formData.append('pace_ratio', pace_ratio);

  const response = await api.post('/running/download-zwift-workout', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    responseType: 'blob'
  });

  return response;
};

/**
 * Download FIT running workout file (.fit format)
 * @param {Object} params - Workout parameters
 * @returns {Promise} FIT file blob
 */
export const downloadRunningFitWorkout = async (params) => {
  const {
    analysis_session_id,
    start_time,
    end_time,
    threshold_pace,
    include_warmup = false,
    warmup_duration = 600,
    pace_ratio = 1.0
  } = params;

  const formData = new FormData();
  formData.append('analysis_session_id', analysis_session_id);
  formData.append('start_time', start_time);
  formData.append('end_time', end_time);
  formData.append('threshold_pace', threshold_pace);
  formData.append('include_warmup', include_warmup);
  formData.append('warmup_duration', warmup_duration);
  formData.append('pace_ratio', pace_ratio);

  const response = await api.post('/running/download-fit-workout', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    responseType: 'blob'
  });

  return response;
};

// ==================== Configuration ====================

/**
 * Get user's running configurations
 * @returns {Promise} Array of running configurations
 */
export const getRunningConfigurations = async () => {
  const response = await api.get('/running/configurations');
  return response.data;
};

/**
 * Create new running configuration
 * @param {Object} config - Configuration data
 * @returns {Promise} Created configuration
 */
export const createRunningConfiguration = async (config) => {
  const response = await api.post('/running/configurations', config);
  return response.data;
};

/**
 * Update existing running configuration
 * @param {number} configId - Configuration ID
 * @param {Object} config - Updated configuration data
 * @returns {Promise} Updated configuration
 */
export const updateRunningConfiguration = async (configId, config) => {
  const response = await api.put(`/running/configurations/${configId}`, config);
  return response.data;
};

// ==================== Saved Files ====================

/**
 * Get user's saved running FIT files
 * @returns {Promise} Array of FIT files
 */
export const getMyRunningFitFiles = async () => {
  const response = await api.get('/running/my-files/fit');
  return response.data;
};

/**
 * Get user's saved running GPX files
 * @returns {Promise} Array of GPX files
 */
export const getMyRunningGpxFiles = async () => {
  const response = await api.get('/running/my-files/gpx');
  return response.data;
};

/**
 * Download a saved running file
 * @param {string} fileType - 'fit' or 'gpx'
 * @param {number} fileId - File ID
 * @returns {Promise} File blob
 */
export const downloadMyRunningFile = async (fileType, fileId) => {
  const response = await api.get(`/running/my-files/${fileType}/${fileId}/download`, {
    responseType: 'blob'
  });
  return response.data;
};

/**
 * Delete a saved running file
 * @param {string} fileType - 'fit' or 'gpx'
 * @param {number} fileId - File ID
 * @returns {Promise} Deletion confirmation
 */
export const deleteMyRunningFile = async (fileType, fileId) => {
  const response = await api.delete(`/running/my-files/${fileType}/${fileId}`);
  return response.data;
};

export const updateRunningFileDescription = async (fileId, description) => {
  const response = await api.patch(`/running/my-files/fit/${fileId}/description`, { description: description || null });
  return response.data;
};

/**
 * Get the user's performance profile — up to 3 secondary reference FIT files
 * used for deep prediction.
 * @returns {Promise<{secondary_fit_file_ids: number[], files: Array}>}
 */
export const getRunningProfile = async () => {
  const response = await api.get('/running/profile');
  return response.data;
};

/**
 * Replace the user's secondary reference FIT file selection (max 3).
 * @param {number[]} secondaryFitFileIds
 * @returns {Promise<{secondary_fit_file_ids: number[]}>}
 */
export const updateRunningProfile = async (secondaryFitFileIds) => {
  const response = await api.put('/running/profile', {
    secondary_fit_file_ids: secondaryFitFileIds,
  });
  return response.data;
};

// ==================== Prediction Accuracy Reports ====================

/**
 * List the user's predictions that can be graded against a given analysis.
 * @param {string} analysisSessionId
 */
export const getAccuracyCandidates = async (analysisSessionId) => {
  const response = await api.get('/running/prediction-accuracy/candidates', {
    params: { analysis_session_id: analysisSessionId },
  });
  return response.data;
};

/** Create + persist an accuracy report comparing a prediction to the actual race. */
export const createAccuracyReport = async (analysisSessionId, predictionSessionId) => {
  const response = await api.post('/running/prediction-accuracy', {
    analysis_session_id: analysisSessionId,
    prediction_session_id: predictionSessionId,
  });
  return response.data;
};

/** List all saved accuracy reports (debug page). */
export const listAccuracyReports = async () => {
  const response = await api.get('/running/prediction-accuracy');
  return response.data;
};

/** Fetch a saved accuracy report by ID (full data). */
export const getAccuracyReport = async (reportId) => {
  const response = await api.get(`/running/prediction-accuracy/${reportId}`);
  return response.data;
};

/** Delete a saved accuracy report. */
export const deleteAccuracyReport = async (reportId) => {
  const response = await api.delete(`/running/prediction-accuracy/${reportId}`);
  return response.data;
};

/** Re-run the prediction with the current algorithm and diff vs actual + stored. */
export const rerunAccuracyDiff = async (reportId) => {
  const response = await api.post(`/running/prediction-accuracy/${reportId}/rerun-diff`);
  return response.data;
};

/** Owner-only: toggle whether this accuracy report is shareable via its link. */
export const setAccuracyReportShared = async (reportId, isPublic) => {
  const response = await api.post(`/running/prediction-accuracy/${reportId}/share`, { is_public: isPublic });
  return response.data;
};

/**
 * Mode 3 (democratic) prediction: predict a race over the user's saved secondary
 * reference files (the "2nd ref" marks in My Files). No main reference needed.
 * @param {{raceId?: number, courseFitFileId?: number}} params
 */
export const predictDemocratic = async ({
  raceId = null, courseFitFileId = null, paceRatio = null,
  stopTimeMultiplier = null, targetTotalTimeS = null, targetStopTimeS = null,
}) => {
  const body = { race_id: raceId, course_fit_file_id: courseFitFileId };
  if (paceRatio != null) body.pace_ratio = paceRatio;
  if (stopTimeMultiplier != null) body.stop_time_multiplier = stopTimeMultiplier;
  // A finish time to hit. The backend solves for the effort ratio that lands on
  // it, so the caller never has to reason about the ratio.
  if (targetTotalTimeS != null) body.target_total_time_s = targetTotalTimeS;
  // Absolute, not a ratio: the number shown already contains the per-checkpoint
  // floors, so a ratio taken against it does not mean what the user thinks.
  if (targetStopTimeS != null) body.target_stop_time_s = targetStopTimeS;
  const response = await api.post('/running/predict-democratic', body);
  return response.data;
};

// ==================== Community Workouts ====================

/**
 * List shared running workouts with filters
 * @param {Object} filters - Filter parameters
 * @returns {Promise} Array of shared workouts
 */
export const listSharedRunningWorkouts = async (filters = {}) => {
  const response = await api.get('/running/community/workouts', { params: filters });
  return response.data;
};

/**
 * Share a running workout to community
 * @param {Object} shareData - Sharing data
 * @returns {Promise} Shared workout confirmation
 */
export const shareRunningWorkout = async (shareData) => {
  const response = await api.post('/running/community/workouts', shareData);
  return response.data;
};

/**
 * Download shared running workout
 * @param {number} workoutId - Shared workout ID
 * @returns {Promise} FIT file blob
 */
export const downloadSharedRunningWorkout = async (workoutId) => {
  const response = await api.get(`/running/community/workouts/${workoutId}/download`, {
    responseType: 'blob'
  });
  return response.data;
};

/**
 * Get user's FIT files with sharing status
 * @returns {Promise} Array of FIT files with sharing info
 */
export const getUserRunningFitFilesWithSharing = async () => {
  const response = await api.get('/running/community/user/files/fit');
  return response.data;
};

/**
 * Unshare a community running workout
 * @param {number} workoutId - Shared workout ID to delete
 * @returns {Promise}
 */
export const unshareRunningWorkout = async (workoutId) => {
  const response = await api.delete(`/running/community/workouts/${workoutId}`);
  return response.data;
};

// ==================== Community GPX ====================

/**
 * List running GPX courses with filters
 * @param {Object} filters - Filter parameters
 * @returns {Promise} Array of GPX courses
 */
export const listRunningGpxCourses = async (filters = {}) => {
  const response = await api.get('/running/community/gpx', { params: filters });
  return response.data;
};

/**
 * Get available filters for GPX courses
 * @returns {Promise} Available filter options
 */
export const getRunningGpxCourseFilters = async () => {
  const response = await api.get('/running/community/gpx/filters');
  return response.data;
};

/**
 * List community running GPX files
 * @param {Object} params - Query parameters
 * @returns {Promise} Array of community GPX files
 */
export const listCommunityRunningGpx = async (params = {}) => {
  const response = await api.get('/running/community/gpx/list', { params });
  return response.data;
};

/**
 * Share a running GPX file to community
 * @param {Object} shareData - Sharing data
 * @returns {Promise} Shared GPX confirmation
 */
export const shareRunningGpx = async (shareData) => {
  const response = await api.post('/running/community/gpx/share', shareData);
  return response.data;
};

/**
 * Download shared running GPX file
 * @param {number} gpxId - Shared GPX ID
 * @returns {Promise} GPX file blob
 */
export const downloadSharedRunningGpx = async (gpxId) => {
  const response = await api.get(`/running/community/gpx/${gpxId}/download`, {
    responseType: 'blob'
  });
  return response.data;
};

/**
 * Get user's GPX files with sharing status
 * @returns {Promise} Array of GPX files with sharing info
 */
export const getUserRunningGpxFilesWithSharing = async () => {
  const response = await api.get('/running/my-files/gpx/with-sharing');
  return response.data;
};

// ==================== Races ====================

/**
 * List available running races with filters
 * @param {Object} filters - Filter parameters
 * @returns {Promise} Array of races
 */
export const listRunningRaces = async (filters = {}) => {
  const response = await api.get('/running/races', { params: filters });
  return response.data;
};

/**
 * Get specific running race details
 * @param {number} raceId - Race ID
 * @returns {Promise} Race details
 */
export const getRunningRace = async (raceId) => {
  const response = await api.get(`/running/races/${raceId}`);
  return response.data;
};

/**
 * Download running race GPX file
 * @param {number} raceId - Race ID
 * @returns {Promise} GPX file blob
 */
export const downloadRunningRaceGpx = async (raceId) => {
  const response = await api.get(`/running/races/${raceId}/gpx`, {
    responseType: 'blob'
  });
  return response.data;
};

// Alias for convenience
export const getRunningRaces = listRunningRaces;

/**
 * Upload GPX file for a race (admin only)
 * @param {number} raceId - Race ID
 * @param {File} file - GPX file
 * @returns {Promise} Upload result
 */
export const uploadRunningRaceGpx = async (raceId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await api.post(`/running/admin/races/${raceId}/upload-gpx`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

/**
 * Delete GPX file for a race (admin only)
 * @param {number} raceId - Race ID
 * @returns {Promise} Delete result
 */
export const deleteRunningRaceGpx = async (raceId) => {
  const response = await api.delete(`/running/admin/races/${raceId}/gpx`);
  return response.data;
};

/**
 * Delete an entire race record (admin only)
 * @param {number} raceId - Race ID
 * @returns {Promise} Delete result
 */
export const deleteRunningRace = async (raceId) => {
  const response = await api.delete(`/running/admin/races/${raceId}`);
  return response.data;
};

/**
 * Import races from a JSON file (admin only)
 * @param {File} file - JSON file with races data
 * @returns {Promise} Import result with added/updated counts
 */
export const importRunningRaces = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post('/running/admin/import-races', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

/**
 * Admin: list one user's stored FIT files (metadata only, newest first).
 *
 * The raw blobs stay on the server — this is the table, not the files.
 */
export const adminListUserFiles = async (userId) => {
  const response = await api.get(`/running/admin/users/${userId}/files`);
  return response.data;
};

/**
 * Admin: download one user's stored FIT file.
 *
 * Returns the raw axios response so the caller keeps the blob and the headers.
 */
export const adminDownloadUserFile = async (userId, fileId) => {
  const response = await api.get(
    `/running/admin/users/${userId}/files/${fileId}/download`,
    { responseType: 'blob' },
  );
  return response;
};

/** Admin: list users (id + email) for pickers. */
/**
 * Re-run the FIT pipeline over stored analysis sessions and overwrite them.
 *
 * A stored session is the output of the parser at the time it ran; nothing in
 * the app recomputes it. After a parser change — pause detection, moving time,
 * vertical speeds — every session keeps its old numbers until this runs.
 * `dryRun` reports what would change without writing.
 *
 * A full pass is long enough to outlive a proxy read timeout, so `limit` and
 * `offset` walk the set in batches. The reply carries `next_offset` — feed it
 * back as `offset` for the next batch, and stop when it comes back null.
 */
export const adminReanalyzeAll = async ({
  dryRun = true, limit = null, offset = 0, userId = null,
} = {}) => {
  const response = await api.post('/running/admin/reanalyze-all', {
    dry_run: dryRun, limit, offset, user_id: userId,
  });
  return response.data;
};

/**
 * Check every race's checkpoint coordinates against its own GPX track, and snap
 * the wrong ones onto the track at their stated distance.
 *
 * `dryRun` reports without writing. `dropBadGpx` also detaches a GPX whose length
 * disagrees with the stated race distance — those races have the wrong course
 * attached, and their checkpoints are never snapped from it.
 */
export const adminAuditCheckpoints = async ({ dryRun = true, raceId = null, dropBadGpx = false } = {}) => {
  const response = await api.post('/running/admin/audit-checkpoints', {
    dry_run: dryRun, race_id: raceId, drop_bad_gpx: dropBadGpx,
  });
  return response.data;
};

export const adminListUsers = async () => {
  const response = await api.get('/running/admin/users');
  return response.data;
};

/**
 * Admin: one row per account — who they are, where they are, what they have done.
 *
 * Aggregated server-side in a single query, so this is one request however many
 * accounts exist.
 */
export const adminUserStats = async () => {
  const response = await api.get('/running/admin/user-stats');
  return response.data;
};

/** Admin: a user's personalised-beta fit history. */
export const adminGetBetaHistory = async (userId) => {
  const response = await api.get(`/running/admin/beta-history/${userId}`);
  return response.data;
};

/**
 * Get parsed GPX points for a race
 * @param {number} raceId - Race ID
 * @returns {Promise} GPX points data
 */
export const getRunningRaceGpxPoints = async (raceId) => {
  const response = await api.get(`/running/races/${raceId}/gpx-points`);
  return response.data;
};

/**
// ==================== Comparison ====================

/**
 * Compare two running workouts (DTW alignment + resampled metrics).
 *
 * @param {string} baseSessionId - analysis_session_id of the base workout (A)
 * @param {{ type: 'upload', file: File } | { type: 'myfile', fileId: number } | { type: 'community', sharedId: number }} source
 *   Source for workout B.
 * @param {boolean} snapBToGpx - Whether to snap B's GPS to the race GPX (if available)
 * @param {boolean|null} applyGpsCorrectionsB - Whether to apply GPS corrections to workout B.
 *   null (the default) means "use whatever B's own analysis used", which is what keeps
 *   B's moving time in the comparison equal to the figure on B's own page. Send a
 *   boolean only when the user has deliberately chosen.
 * @param {boolean} snapAToGpx - Whether to snap A's GPS to the race GPX (if available) (default: false)
 * @param {boolean} applyGpsCorrectionsA - Whether to apply GPS corrections to workout A (default: true)
 * @returns {Promise<Object>} RunningComparisonResult
 */
export const compareRunningWorkout = async (
  baseSessionId,
  source,
  snapBToGpx = false,
  applyGpsCorrectionsB = null,
  snapAToGpx = false,
  applyGpsCorrectionsA = true
) => {
  const formData = new FormData();
  formData.append('base_session_id', baseSessionId);
  // Convert booleans to "true"/"false" strings for FastAPI Form parsing
  formData.append('snap_b_to_gpx', snapBToGpx ? 'true' : 'false');
  // Omitted when null so the backend falls back to B's own stored setting.
  if (applyGpsCorrectionsB !== null && applyGpsCorrectionsB !== undefined) {
    formData.append('apply_gps_corrections_b', applyGpsCorrectionsB ? 'true' : 'false');
  }
  formData.append('snap_a_to_gpx', snapAToGpx ? 'true' : 'false');
  formData.append('apply_gps_corrections_a', applyGpsCorrectionsA ? 'true' : 'false');

  if (source.type === 'upload') {
    formData.append('comparison_fit_file', source.file);
  } else if (source.type === 'myfile') {
    formData.append('comparison_file_id', source.fileId);
  } else if (source.type === 'community') {
    formData.append('comparison_shared_id', source.sharedId);
  } else if (source.type === 'prediction') {
    formData.append('comparison_prediction_id', source.predictionId);
  } else {
    throw new Error('Unknown comparison source type');
  }

  const response = await api.post('/running/compare', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

/**
 * Combine two FIT files into one (admin only)
 * @param {File} file1 - First FIT file
 * @param {File} file2 - Second FIT file
 * @returns {Promise<Blob>} Combined FIT file as blob
 */
export const combineFitFiles = async (file1, file2) => {
  const formData = new FormData();
  formData.append('file1', file1);
  formData.append('file2', file2);

  const response = await api.post('/running/admin/combine-fit-files', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    responseType: 'blob'
  });
  return response.data;
};

// ==================== Past Comparisons ====================

/**
 * Get past comparisons for a session.
 * @param {string} sessionId - The base session ID
 * @returns {Promise<Array>} Array of comparison metadata
 */
export const getPastComparisonsForSession = async (sessionId) => {
  const response = await api.get(`/running/comparisons/for-session/${sessionId}`);
  return response.data;
};

export const deleteRunningComparison = async (comparisonId) => {
  const response = await api.delete(`/running/comparisons/${comparisonId}`);
  return response.data;
};

/**
 * Get full comparison data by ID.
 * @param {number} comparisonId - The comparison ID
 * @returns {Promise<Object>} Complete comparison result
 */
export const getComparisonById = async (comparisonId) => {
  const response = await api.get(`/running/comparisons/${comparisonId}`);
  return response.data;
};

// ==================== Race Prediction ====================

/**
 * Predict race performance from a reference FIT file and target race GPX.
 * Auto-saves the result; returns from cache on repeated calls with same inputs.
 *
 * @param {Object} params
 * @param {File|null}   params.referenceFit         - Reference FIT file (or use fitFileId)
 * @param {number|null} params.fitFileId            - Saved RunningFile ID (alternative to referenceFit)
 * @param {File|Blob|null} params.raceGpx           - Target race GPX file (or use raceId)
 * @param {number|null} params.raceId               - Race catalog ID (backend fetches GPX if set)
 * @param {File|null}   params.referenceRaceGpx     - Optional reference race GPX (for stop learning)
 * @param {Object}      params.options              - Stop defaults and tuning params
 * @returns {Promise<Object>} Prediction result
 */
export const predictRunningRace = async ({
  referenceFit = null,
  fitFileId = null,
  raceGpx = null,
  raceId = null,
  referenceRaceGpx = null,
  // Course-as-FIT options (alternative to raceGpx / raceId):
  courseFitFileId = null,    // user's own saved FIT file used as course terrain
  courseFitFile = null,      // File or Blob (uploaded GPX-alt or downloaded club FIT)
  courseFitFilename = null,  // display name to use as race_name (overrides Blob.name)
  options = {},
  deepPrediction = false,    // use secondary reference files to calibrate coefficients
  parallelAlgorithm = false, // also run the experimental parallel predictor (returned as parallel_v2)
  parallelMode = null,       // 'single' | 'main_secondary' | 'democratic' (auto if null)
  algorithm = null,          // 'v2' → parallel predictor is the PRIMARY result; else live pipeline
}) => {
  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const body = { race_id: raceId, options };

  if (deepPrediction) {
    body.deep_prediction = true;
  }
  if (parallelAlgorithm) {
    body.parallel_algorithm = true;
  }
  if (algorithm === 'v2') {
    body.algorithm = 'v2';
  }
  if (parallelMode) body.parallel_mode = parallelMode;

  if (fitFileId) {
    body.fit_file_id = fitFileId;
  } else if (referenceFit) {
    body.reference_fit_base64 = await toBase64(referenceFit);
    body.fit_filename = referenceFit.name;
  }

  // Course resolution: FIT-as-course takes priority over race GPX / race_id
  if (courseFitFileId) {
    body.course_fit_file_id = courseFitFileId;
  } else if (courseFitFile) {
    body.course_fit_base64 = await toBase64(courseFitFile);
    body.course_fit_filename = courseFitFilename || courseFitFile.name || 'course.fit';
  } else if (raceGpx) {
    body.race_gpx_base64 = await toBase64(raceGpx);
  }
  // else: backend uses race_id to fetch GPX

  if (referenceRaceGpx) {
    body.reference_race_gpx_base64 = await toBase64(referenceRaceGpx);
  }

  const response = await api.post('/running/predict-race', body);
  return response.data;
};

/**
 * List the current user's saved race predictions (metadata only).
 * @returns {Promise<Array>} Array of prediction metadata
 */
export const getMyRunningPredictions = async () => {
  const response = await api.get('/running/predictions');
  return response.data;
};

/**
 * Load a saved race prediction by ID (no re-computation).
 * @param {string} predictionId - Prediction session ID
 * @returns {Promise<Object>} Full prediction result
 */
export const getRunningPrediction = async (predictionId) => {
  const response = await api.get(`/running/predictions/${predictionId}`);
  return response.data;
};

export const listRunningPredictions = async (raceId = null) => {
  // raceId narrows server-side. Filtering the response instead would filter a
  // capped page, so a race whose predictions all sit past the cap looks empty.
  const response = await api.get('/running/predictions', {
    params: raceId != null ? { race_id: raceId } : undefined,
  });
  return response.data;
};

export const deleteRunningPrediction = async (predictionId) => {
  const response = await api.delete(`/running/predictions/${predictionId}`);
  return response.data;
};

// ==================== Live Tracking ====================

/**
 * Attach a UTMB Live runner link to a race prediction, start following it, and
 * mint the public share link for the dedicated live-tracking page.
 * @param {string} predictionId - Prediction session ID
 * @param {string} url - UTMB Live runner URL, e.g. https://live.utmb.world/<event>/<year>/runners/<bib>
 * @param {number|null} clubPredictionId - Set when attached from a club-shared prediction view
 * @returns {Promise<Object>} { share_id, share_url, link_id }
 */
export const attachLiveTracking = async (predictionId, url, clubPredictionId = null) => {
  const response = await api.post(`/running/predictions/${predictionId}/live-tracking`, {
    url,
    club_prediction_id: clubPredictionId,
  });
  return response.data;
};

/**
 * Attach a TORX runner (TOR330/TOR450/TOR130/TOR100/TOR30) by race + bib.
 * TORX publishes no per-runner URL, so there is nothing to paste — the bib is validated
 * server-side against the race's entrant list.
 * @param {string} predictionId
 * @param {string} raceId - TORX gara id, e.g. "1667" (TOR130)
 * @param {string} bib
 * @param {string|null} year - season, defaults server-side to the current year
 * @param {number|null} clubPredictionId
 * @returns {Promise<Object>} { share_id, share_url, link_id }
 */
export const attachTorxLiveTracking = async (predictionId, raceId, bib, year = null, clubPredictionId = null) => {
  const response = await api.post(`/running/predictions/${predictionId}/live-tracking`, {
    provider: 'torx',
    race_id: raceId,
    bib,
    year,
    club_prediction_id: clubPredictionId,
  });
  return response.data;
};

/**
 * TORX races for a season, for the race picker.
 * @param {string|null} year
 * @returns {Promise<Object>} { year, races: [{ gara_id, name, entrants, checkpoints }] }
 */
export const getTorxRaces = async (year = null) => {
  const response = await api.get('/running/live-tracking/torx/races', { params: year ? { year } : {} });
  return response.data;
};

/**
 * Poll a live tracking link (safe to call frequently — cached server-side ~2 min).
 * @param {number} linkId
 * @returns {Promise<Object>} Live snapshot
 */
export const getLiveTracking = async (linkId) => {
  const response = await api.get(`/running/live-tracking/${linkId}`);
  return response.data;
};

/**
 * Stop following a runner (only the person who attached the link can unlink it).
 * @param {number} linkId
 */
export const deleteLiveTracking = async (linkId) => {
  const response = await api.delete(`/running/live-tracking/${linkId}`);
  return response.data;
};

/**
 * Create a public, no-login share link for a live tracking view.
 * @param {number} linkId
 * @returns {Promise<Object>} { share_id, share_url }
 */
export const shareLiveTracking = async (linkId) => {
  const response = await api.post(`/running/live-tracking/${linkId}/share`);
  return response.data;
};

/**
 * Get a shared live tracking view (public, no login required).
 * @param {string} shareId
 * @returns {Promise<Object>} Live snapshot + full prediction payload
 */
export const getSharedLiveTracking = async (shareId) => {
  const response = await api.get(`/running/shared/live-tracking/${shareId}`);
  return response.data;
};
